<template>
  <section class="section home-page">
    <div class="container">
      <header class="home-header">
        <div>
          <h1 class="title is-4">
            {{ $t('home.greeting', { name: `${currentUser.firstname} ${currentUser.lastname}` }) }}
          </h1>
          <p class="subtitle is-6 has-text-grey">{{ $t('home.intro') }}</p>
        </div>
        <b-tag :type="badge.type" size="is-medium">
          {{ badge.label ? $t(badge.label) : badge.text }}
        </b-tag>
      </header>

      <router-link :to="{ name: 'process', params: { step: 1 } }" class="home-primary">
        <span class="home-primary-icon">
          <i class="fas fa-clipboard-check"></i>
        </span>
        <span class="home-primary-text">
          <span class="home-primary-title">{{ $t('home.startAudit') }}</span>
          <span class="home-primary-description">{{ $t('home.startAuditDescription') }}</span>
        </span>
        <span class="home-primary-arrow">
          <i class="fas fa-arrow-right"></i>
        </span>
      </router-link>

      <router-link v-if="role.limited" :to="{ name: 'payment' }" class="home-upgrade">
        <span class="home-upgrade-icon">
          <i class="fas fa-star"></i>
        </span>
        <span class="home-upgrade-text">
          <span class="home-upgrade-title">{{ $t('home.upgradeTitle') }}</span>
          <span class="home-upgrade-description">{{ $t('home.cards.payment') }}</span>
        </span>
        <span class="button is-success home-upgrade-button">
          {{ $t('button.goToPaidMembership') }}
        </span>
      </router-link>

      <div v-for="group in groups" :key="group.key" class="home-group">
        <h2 v-if="group.title" class="home-group-title">{{ $t(group.title) }}</h2>
        <hr v-else class="home-group-divider" />
        <div class="columns is-multiline">
          <div
            v-for="item in group.items"
            :key="item.route || item.href"
            class="column is-6-tablet is-4-desktop is-3-widescreen"
          >
            <menu-link :item="item" class="home-card">
              <span class="home-card-icon">
                <i :class="`fas fa-${item.icon}`"></i>
              </span>
              <span class="home-card-text">
                <span class="home-card-title">
                  {{ label(item) }}
                  <i v-if="item.href" class="fas fa-external-link-alt home-card-external"></i>
                </span>
                <span class="home-card-description">{{ $t(item.description) }}</span>
              </span>
            </menu-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { visibleMenu, membershipBadge } from '../_helpers/menu'
import MenuLink from '../components/MenuLink.vue'

export default {
  components: { MenuLink },
  computed: {
    ...mapGetters(['currentUser', 'role']),
    // Items with home: false have a block of their own above the cards.
    groups () {
      return visibleMenu({ role: this.role, currentUser: this.currentUser })
        .map(group => ({ ...group, items: group.items.filter(item => item.home !== false) }))
        .filter(group => group.items.length > 0)
    },
    badge () {
      return membershipBadge({ role: this.role, currentUser: this.currentUser })
    }
  },
  methods: {
    label (item) {
      return item.label.map(key => this.$t(key)).join(' ')
    }
  }
}
</script>

<style scoped>
.home-page {
  padding-top: 2rem;
}

.home-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1.5rem;
  margin-bottom: 1.5rem;
}

.home-header .title {
  margin-bottom: 0.5rem;
}

/* Bulma pulls a subtitle up under a title; here they sit apart. */
.home-header .subtitle {
  margin-top: 0;
}

.home-primary {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.75rem 2rem;
  margin-bottom: 2.5rem;
  border-radius: 8px;
  background: #31b4cb;
  color: #fff;
  box-shadow: 0 6px 20px rgba(49, 180, 203, 0.3);
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out, background 0.15s ease-out;
}

.home-primary:hover,
.home-primary:focus {
  color: #fff;
  background: #2aa2b7;
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(49, 180, 203, 0.4);
}

/* The upgrade for free users: right under the audit button, green like the licensed
   members' badge, lighter so that starting an audit stays the main action. */
.home-primary + .home-upgrade {
  margin-top: -1.25rem;
}

.home-upgrade {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 2rem;
  margin-bottom: 2.5rem;
  border: 1px solid #23d160;
  border-radius: 8px;
  background: #effaf3;
  color: #4a4a4a;
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
}

.home-upgrade:hover,
.home-upgrade:focus {
  color: #4a4a4a;
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(35, 209, 96, 0.2);
}

.home-upgrade-icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #23d160;
  color: #fff;
  font-size: 1.35rem;
}

.home-upgrade-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.home-upgrade-title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  color: #257942;
}

.home-upgrade-description {
  margin-top: 0.25rem;
}

.home-upgrade-button {
  flex: none;
}

.home-primary-icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  font-size: 1.75rem;
}

.home-primary-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.home-primary-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
}

.home-primary-description {
  margin-top: 0.35rem;
  opacity: 0.9;
}

.home-primary-arrow {
  flex: none;
  font-size: 1.5rem;
  transition: transform 0.15s ease-out;
}

.home-primary:hover .home-primary-arrow {
  transform: translateX(4px);
}

.home-group {
  margin-bottom: 1.5rem;
}

.home-group-divider {
  height: 1px;
  margin: 0.5rem 0 1.5rem;
  background: #dbdbdb;
}

.home-group-title {
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7a7a7a;
}

.home-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  height: 100%;
  padding: 1.25rem;
  border: 1px solid #ebedf2;
  border-radius: 8px;
  background: #fff;
  color: #4a4a4a;
  transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out, transform 0.15s ease-out;
}

.home-card:hover,
.home-card:focus {
  color: #4a4a4a;
  border-color: #31b4cb;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.home-card-icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 8px;
  background: rgba(49, 180, 203, 0.12);
  color: #31b4cb;
  font-size: 1.15rem;
}

.home-card-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.home-card-title {
  font-weight: 700;
  color: #363636;
}

.home-card-external {
  margin-left: 0.25rem;
  font-size: 0.7rem;
  color: #7a7a7a;
}

.home-card-description {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #7a7a7a;
}

@media screen and (max-width: 768px) {
  .home-page {
    padding: 1.5rem 1rem;
  }

  .home-primary {
    padding: 1.25rem;
    gap: 1rem;
  }

  .home-primary-icon {
    width: 3rem;
    height: 3rem;
    font-size: 1.35rem;
  }

  .home-primary-title {
    font-size: 1.2rem;
  }

  .home-primary-arrow {
    display: none;
  }

  .home-upgrade {
    flex-wrap: wrap;
    padding: 1.25rem;
    gap: 1rem;
  }

  .home-upgrade-text {
    flex-basis: calc(100% - 4rem);
  }

  .home-upgrade-button {
    width: 100%;
  }
}
</style>
